import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { financialsService } from '@/services/financials-graphql-service';
import type { Project } from '@/types/financials';

interface ProjectPickerProps {
  readonly value?: string;
  readonly displayValue?: string;
  readonly onValueChange: (code: string, name: string) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
}

export function ProjectPicker({
  value,
  displayValue,
  onValueChange,
  disabled = false,
  placeholder = 'Select project...',
  className,
}: Readonly<ProjectPickerProps>) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await financialsService.getProjects();
        setProjects(data);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleSelect = (project: Project) => {
    onValueChange(project.code, project.name);
    setOpen(false);
  };

  const getSelectedDisplay = () => {
    if (loading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (value) {
      const selectedProject = projects.find(p => p.code === value);
      return selectedProject
        ? `${selectedProject.code} - ${selectedProject.name}`
        : displayValue ?? value;
    }
    return placeholder;
  };

  if (disabled) {
    return (
      <Input
        value={displayValue ?? value ?? ''}
        readOnly
        className={cn('bg-muted', className)}
      />
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal bg-white"
            disabled={loading}
          >
            {getSelectedDisplay()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search projects..." />
            <CommandEmpty>No project found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-y-auto">
              {projects.map((project) => (
                <CommandItem
                  key={project.code}
                  value={`${project.code} ${project.name}`}
                  onSelect={() => handleSelect(project)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === project.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="font-medium">{project.code}</span>
                  <span className="ml-2 text-muted-foreground">{project.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load projects</p>
      )}
    </div>
  );
}
